/**
 * Project settings (Story 514): read a project's strictness and let the
 * owner change it.
 *
 * Talks to GET and PATCH /api/v1/projects/:slug/settings directly with
 * fetch rather than through api.fetch, because the dialog needs the status
 * code and the body of a refusal: 403 (not the owner), 409 (someone else
 * changed it first, body carries the current value), 400 (value refused).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config } from '../config';

export type Strictness = 'lenient' | 'standard' | 'strict';
/** Story 537, widened by 545: which validation service answers the qualitative content gates. */
export type ContentValidationService = 'gemini' | 'typesafe' | 'fireworks';

/** What the read route returns. */
export interface ProjectSettings {
  project: string;
  strictness: Strictness;
  effective: { value: Strictness; source: string };
  role: string;
  permitted: Strictness[];
  /** One sentence per level on what it does; shared with the manage tool's confirmation. */
  consequence: Record<Strictness, string>;
  /** Story 537 */
  content_validation_service: ContentValidationService;
  validation_services_permitted: ContentValidationService[];
  /** Task 537.43: per gate, the criteria the TypeSafe thresholds disable; null where they cannot be read. */
  typesafe_disabled_criteria?: TypesafeDisabledCriteria;
  /** Story 556: whether finishing a task in this project needs a commit. */
  commit_evidence_required: boolean;
  /** Story 546: the project's own AI service, or null when checks run on Ceetrix's account. */
  ai_service?: AiServiceSummary | null;
}

/** Story 546: one of the provider's effort levels; absent means the provider's default. */
export type AiServiceEffort = 'low' | 'medium' | 'high';

/** Story 546, task 546.29: the last check the project's own model refused; information, not an alert. */
export interface AiServiceCheckFailure {
  /** The check's kind as the worker records it: prd, design, story-title, evidence, diff_qa, g9_extraction, g10_body, design_coverage. */
  check: string;
  /** The story or task it was for; empty when none was recorded. */
  ref: string;
  at: string;
  /** The provider's own words. */
  words: string;
}

/** Story 546: what the settings read says about the project's own AI service; never the key. */
export interface AiServiceSummary {
  format: 'anthropic' | 'openai';
  address: string;
  model: string;
  effort: AiServiceEffort | null;
  /** Anthropic-compatible only: the levels the model supports; null when unknown. */
  effort_levels: AiServiceEffort[] | null;
  key_last4: string;
  added_at: string;
  added_by: string | null;
  state: 'ceetrix' | 'working' | 'paused' | 'customer_fix' | 'stopped' | 'unusable';
  /** Whether the workspace alert shows: the customer has something to fix. */
  alert: boolean;
  reason: string | null;
  since: string | null;
  last_worked_at: string | null;
  next_try_at: string | null;
  last_check_failure: AiServiceCheckFailure | null;
}

/** Story 546: a change to the service; a missing key means the saved one. */
export interface AiServiceChange {
  address: string;
  model: string;
  effort: AiServiceEffort | null;
  key?: string;
}

/** Story 546: the answer to a change or a Test. On a refusal, `field` says where the reason belongs. */
export interface AiServiceResult {
  ok: boolean;
  field?: 'address' | 'key' | 'model' | 'effort' | 'general';
  message?: string;
  ai_service?: AiServiceSummary | null;
}

/** Story 556: the commit requirement. A body carrying commit_evidence_required changes that setting only. */
export interface CommitRequirementWrite {
  commit_evidence_required: boolean;
  expected_previous_commit: boolean;
}

export interface CommitRequirementChange {
  commit_evidence_required: boolean;
  previous_commit: boolean;
  /** Present when the change was saved but could not be added to the change history. */
  warning?: string;
}

export type ValidationGate = 'prd' | 'design' | 'evidence' | 'story-title';
/** A rule the TypeSafe thresholds disable: its internal name, and its checklist wording for a person (537.46). */
export interface TypesafeDisabledCriterion {
  name: string;
  rule: string;
}
export type TypesafeDisabledCriteria = Record<ValidationGate, TypesafeDisabledCriterion[] | null>;

/** Story 537: the validation service switch. A body carrying content_validation_service changes the validation service only. */
export interface ProjectSettingsServiceWrite {
  content_validation_service: ContentValidationService;
  expected_previous_service: ContentValidationService;
}

export interface ProjectSettingsServiceChange {
  content_validation_service: ContentValidationService;
  previous_service: ContentValidationService;
  warning?: string;
}

/** What the write route takes and returns. */
export interface ProjectSettingsWrite {
  strictness: Strictness;
  expected_previous: Strictness;
}

export interface ProjectSettingsChange {
  strictness: Strictness;
  previous: Strictness;
  /** Present when the change was saved but could not be added to the change history. */
  warning?: string;
}

export interface ProjectSettingsErrorBody {
  error?: string;
  current?: Strictness;
  permitted?: string[];
  /** Story 537: what is current on a conflict. */
  current_service?: ContentValidationService;
  /** Story 556: the commit requirement in force on a conflict. */
  current_commit?: boolean;
  keys?: string[];
  script?: string;
}

/** A refused request, with the status and body the dialog decides on. */
export class ProjectSettingsRequestError extends Error {
  readonly status: number;
  readonly body: ProjectSettingsErrorBody;

  constructor(status: number, body: ProjectSettingsErrorBody) {
    super(body.error ?? `Project settings request failed (${status})`);
    this.name = 'ProjectSettingsRequestError';
    this.status = status;
    this.body = body;
  }
}

/** Query key factory */
export const projectSettingsKeys = {
  all: ['project-settings'] as const,
  detail: (projectSlug: string) => [...projectSettingsKeys.all, projectSlug] as const,
};

function settingsUrl(projectSlug: string): string {
  return `${config.apiUrl}/api/v1/projects/${encodeURIComponent(projectSlug)}/settings`;
}

async function errorBody(response: Response): Promise<ProjectSettingsErrorBody> {
  try {
    return (await response.json()) as ProjectSettingsErrorBody;
  } catch {
    return {};
  }
}

async function fetchProjectSettings(projectSlug: string): Promise<ProjectSettings> {
  const response = await fetch(settingsUrl(projectSlug), { credentials: 'include' });
  if (!response.ok) {
    throw new ProjectSettingsRequestError(response.status, await errorBody(response));
  }
  return (await response.json()) as ProjectSettings;
}

async function patchProjectSettings(
  projectSlug: string,
  input: ProjectSettingsWrite
): Promise<ProjectSettingsChange> {
  const response = await fetch(settingsUrl(projectSlug), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new ProjectSettingsRequestError(response.status, await errorBody(response));
  }
  return (await response.json()) as ProjectSettingsChange;
}

/**
 * The project's settings as the viewer may see them. Always refetched when
 * the dialog opens (staleTime 0): the value can change from another
 * session, and a stale read is exactly what the conflict path guards.
 */
export function useProjectSettings(projectSlug: string) {
  return useQuery({
    queryKey: projectSettingsKeys.detail(projectSlug),
    queryFn: () => fetchProjectSettings(projectSlug),
    enabled: !!projectSlug,
    staleTime: 0,
  });
}

async function patchProjectService(projectSlug: string, input: ProjectSettingsServiceWrite): Promise<ProjectSettingsServiceChange> {
  const response = await fetch(settingsUrl(projectSlug), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new ProjectSettingsRequestError(response.status, await errorBody(response));
  }
  return (await response.json()) as ProjectSettingsServiceChange;
}

/** Story 537: the owner's validation service switch. On success the cached read is updated in place. */
export function useUpdateProjectService(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectSettingsServiceWrite) => patchProjectService(projectSlug, input),
    onSuccess: (change) => {
      queryClient.setQueryData<ProjectSettings>(projectSettingsKeys.detail(projectSlug), (old) =>
        old ? { ...old, content_validation_service: change.content_validation_service } : old
      );
    },
  });
}

/** Owner's change. On success the cached read is updated in place. */
export function useUpdateProjectSettings(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectSettingsWrite) => patchProjectSettings(projectSlug, input),
    onSuccess: (change) => {
      queryClient.setQueryData<ProjectSettings>(projectSettingsKeys.detail(projectSlug), (old) =>
        old
          ? { ...old, strictness: change.strictness, effective: { ...old.effective, value: change.strictness } }
          : old
      );
    },
  });
}

async function patchCommitRequirement(projectSlug: string, input: CommitRequirementWrite): Promise<CommitRequirementChange> {
  const response = await fetch(settingsUrl(projectSlug), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new ProjectSettingsRequestError(response.status, await errorBody(response));
  }
  return (await response.json()) as CommitRequirementChange;
}

/** Story 556: the owner's commit requirement switch. On success the cached read is updated in place. */
export function useUpdateCommitRequirement(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CommitRequirementWrite) => patchCommitRequirement(projectSlug, input),
    onSuccess: (change) => {
      queryClient.setQueryData<ProjectSettings>(projectSettingsKeys.detail(projectSlug), (old) =>
        old ? { ...old, commit_evidence_required: change.commit_evidence_required } : old
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Story 546: AI Services. A refused change answers 422 with { ok: false, field,
// message }, which is the result the panel shows, not an error; anything else
// that is not ok throws ProjectSettingsRequestError as the other writes do.
// ---------------------------------------------------------------------------

const HTTP_UNPROCESSABLE = 422;
const HTTP_SERVICE_UNAVAILABLE = 503;

function aiServiceUrl(projectSlug: string, suffix = ''): string {
  return `${settingsUrl(projectSlug)}/ai-service${suffix}`;
}

async function aiServiceRequest(url: string, init: RequestInit): Promise<AiServiceResult> {
  const response = await fetch(url, { credentials: 'include', ...init });
  if (response.ok || response.status === HTTP_UNPROCESSABLE || response.status === HTTP_SERVICE_UNAVAILABLE) {
    return (await response.json()) as AiServiceResult;
  }
  throw new ProjectSettingsRequestError(response.status, await errorBody(response));
}

function useAiServiceMutation<TInput>(projectSlug: string, request: (input: TInput) => Promise<AiServiceResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (result) => {
      if (result.ai_service === undefined) return;
      queryClient.setQueryData<ProjectSettings>(projectSettingsKeys.detail(projectSlug), (old) =>
        old ? { ...old, ai_service: result.ai_service } : old
      );
    },
  });
}

/** Story 546: the owner's change; checked by the server before anything is saved. */
export function useSaveAiService(projectSlug: string) {
  return useAiServiceMutation(projectSlug, (input: AiServiceChange) =>
    aiServiceRequest(aiServiceUrl(projectSlug), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

/** Story 546: the owner chooses Ceetrix again; the saved service is deleted. */
export function useRemoveAiService(projectSlug: string) {
  return useAiServiceMutation(projectSlug, () => aiServiceRequest(aiServiceUrl(projectSlug), { method: 'DELETE' }));
}

/** Story 546: any member runs one check now. */
export function useTestAiService(projectSlug: string) {
  // The worker refuses any POST that is not JSON (security/validation.ts), so
  // Test sends an empty JSON object (546.30).
  return useAiServiceMutation(projectSlug, () => aiServiceRequest(aiServiceUrl(projectSlug, '/test'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  }));
}
